import { type FunctionComponent, useContext, useEffect, useState } from 'react';

import { Card, Elevation } from '@blueprintjs/core';
import { ForecastContext, UserContext } from 'App';

import i18n from 'i18n/i18n';
import { type DunningSelectionForecast, type ForecastFilter, RIGHTS } from 'interfaces/Interfaces';
import { Switch } from 'lib/Switch';
import {
  downloadFilteredDunningForecastCSV,
  getFilteredDunningForecast,
  updateSystemProperties,
} from 'services/ApiService';

import DunningForecast from './DunningForecast';
import { isDunningForecastEnabled } from './forecastAccess';
import { DEFAULT_FORECAST_FILTER, ForecastFilters } from './ForecastFilters';
import { alertToast } from '../Toast/AlertToast';

import './DunningForecastPage.style.sass';

function isDefaultForecastFilter(filter: ForecastFilter) {
  return (
    filter.forecastDayOffset === DEFAULT_FORECAST_FILTER.forecastDayOffset &&
    !filter.rulesetIds?.length &&
    !filter.dunningLevelNames?.length &&
    !filter.actionTypes?.length
  );
}

const DunningForecastPage: FunctionComponent = () => {
  const user = useContext(UserContext);
  const { forecastProperty, refreshForecastProperty } = useContext(ForecastContext);
  const [filter, setFilter] = useState<ForecastFilter>(DEFAULT_FORECAST_FILTER);
  const [appliedFilter, setAppliedFilter] = useState<ForecastFilter>(DEFAULT_FORECAST_FILTER);
  const [forecast, setForecast] = useState<DunningSelectionForecast>();
  const [forecastLoading, setForecastLoading] = useState(false);
  const [forecastError, setForecastError] = useState<unknown>();
  const [refreshCount, setRefreshCount] = useState(0);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);

  const enabled = isDunningForecastEnabled(forecastProperty);
  const canUpdateForecast = user.rights.includes(RIGHTS.ACCESS_SETTINGS);

  useEffect(() => {
    refreshForecastProperty();
  }, [refreshForecastProperty]);

  useEffect(() => {
    if (!enabled) {
      setForecast(undefined);
      setForecastError(undefined);
      setForecastLoading(false);
      return () => {};
    }

    const abortController = new AbortController();
    setForecastLoading(true);
    setForecastError(undefined);
    getFilteredDunningForecast(appliedFilter, abortController)
      .then(setForecast)
      .catch(error => {
        if (abortController.signal.aborted) return;
        setForecastError(error);
        alertToast(error);
      })
      .finally(() => {
        if (!abortController.signal.aborted) setForecastLoading(false);
      });

    return () => {
      abortController.abort();
    };
  }, [enabled, appliedFilter, refreshCount]);

  function updateForecastEnabled(checked: boolean) {
    if (!forecastProperty || toggleLoading) return;

    const updatedProperty = { ...forecastProperty, value: String(checked) };
    setToggleLoading(true);
    updateSystemProperties([updatedProperty])
      .then(refreshForecastProperty)
      .catch(alertToast)
      .finally(() => setToggleLoading(false));
  }

  function downloadForecast() {
    if (!enabled) return;

    setDownloadLoading(true);
    downloadFilteredDunningForecastCSV(appliedFilter)
      .catch(alertToast)
      .finally(() => setDownloadLoading(false));
  }

  return (
    <div className="dunning-forecast-page">
      <div className="title">{i18n.t('dunning-forecast.title')}</div>
      <Card className="forecast-card" interactive={false} elevation={Elevation.ONE}>
        {canUpdateForecast && forecastProperty && (
          <div className="forecast-toggle">
            <Switch
              isChecked={enabled}
              onChange={toggleLoading ? undefined : checked => updateForecastEnabled(checked)}>
              {i18n.t(`dunning-forecast.toggle-${enabled ? 'disable' : 'enable'}`)}
            </Switch>
          </div>
        )}
        {enabled && (
          <ForecastFilters
            filter={filter}
            onRefresh={nextFilter => {
              setFilter(nextFilter);
              setAppliedFilter(nextFilter);
              setRefreshCount(count => count + 1);
            }}
            onReset={() => {
              setFilter(DEFAULT_FORECAST_FILTER);
              if (isDefaultForecastFilter(appliedFilter)) return;

              setAppliedFilter(DEFAULT_FORECAST_FILTER);
              setRefreshCount(count => count + 1);
            }}
          />
        )}
        <DunningForecast
          enabled={enabled}
          updatedAt={forecastProperty?.updated}
          forecast={forecast}
          forecastLoading={enabled && forecastLoading}
          error={forecastError}
          retry={() => setRefreshCount(count => count + 1)}
          download={downloadForecast}
          downloadLoading={downloadLoading}
        />
      </Card>
    </div>
  );
};

export default DunningForecastPage;
